import styles from "./styles.module.scss";
import { UsersGridColumnsHeaders, Columns } from "./data";
import CmsTileUser from "./cms-tile-user/cms-tile-user";
import { Routes } from "routes";
import { IProfileDataModel } from "models/dto/ZoomiLxp/Models/Profile/IProfileDataModel";
import { useTabletGridColumns } from "hooks/useTabletGridColumns";
import { CmsUsersFilter } from "./cms-users-filter";
import CmsSpreadsheet from "../cms-spreadsheet/cms-spreadsheet";
import { useAppStore } from "store";
import { observer } from "mobx-react";
import { useBulkSelections } from "../cms-courses/useBulkSelections";
import { PermissionConstantsRead } from "models/dto/ZoomiLxp/Utilities/Constants/PermissionConstantsRead";
import { useCallback, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import Button from "components/base/button/button";
import { modalDialogService } from "components/base/modal-dialog/modal-dialog";
import { AddUsersModal } from "./add-users-modal";
import { Prompt } from "react-router";
import { infoMessages } from "constants/messages";
import { IAssignmentAccountSelectionModel } from "models/dto/ZoomiLxp/Models/BulkOperations/AssignmentBulkOperation/IAssignmentAccountSelectionModel";
import { IPreviewGroupMemberItem } from "models/dto/ZoomiLxp/Models/GroupModels/Preview/IPreviewGroupMemberItem";
import { ICompanyRefModel } from "models/dto/ZoomiLxp/Models/CompanyModels/ICompanyRefModel";
import { IGetRecordsResponse } from "models/dto/ZoomiLxp/Models/Common/IGetRecordsResponse";
import { IFileModel } from "models/dto/ZoomiLxp/Models/Common/IFileModel";
import { SelectionAccountsType } from "models/dto/ZoomiLxp/Utilities/Enumerations/SelectionAccountsType";
import Tooltip from "components/base/tooltip/tooltip";
import { ReactComponent as InfoIcon } from "assets/icons/ic_info.svg";
import { IPreviewGroupMembersModel } from "models/dto/ZoomiLxp/Models/GroupModels/Preview/IPreviewGroupMembersModel";

interface ICmsUsersProps {
	isHideFilter?: boolean;
	enableGroupActions?: boolean;
}

interface IGroupMembersDelta {
	addSelections: IAssignmentAccountSelectionModel[];
	removeAccountIds: number[];
}

const previewMembersTake = 20;
const getEmptyMembersDelta = (): IGroupMembersDelta => ({
	addSelections: [],
	removeAccountIds: [],
});

const toProfileData = (member: IPreviewGroupMemberItem): IProfileDataModel => ({
	id: member.id,
	isActive: true,
	firstName: member.displayName,
	lastName: "",
	displayName: member.displayName,
	email: member.email,
	userPicture: (member.thumbnail as IFileModel) ?? undefined,
	roles: [],
	jobTitle: "",
	company: member.company as ICompanyRefModel,
	country: "",
	city: "",
	description: "",
	registringDate: "",
	likesCount: 0,
	isLiked: false,
	isFollowed: false,
	expertises: [],
	groupIds: [],
	message: "",
	permissions: [],
	mfaEnabled: false,
});

const CmsUsers = ({ isHideFilter, enableGroupActions }: ICmsUsersProps) => {
	const { accountStore, bulkOperationsStore, searchStore, toasterStore, groupStore } = useAppStore();
	const [membersDelta, setMembersDelta] = useState<IGroupMembersDelta>(getEmptyMembersDelta);
	const [isSaving, setIsSaving] = useState(false);
	const snapshotTokenRef = useRef<string | null>(null);
	const cursorRef = useRef<string | null>(null);
	const loadedCountRef = useRef(0);
	const previewKeyRef = useRef("");
	const { addSelections, removeAccountIds } = membersDelta;
	const isGridChanged = !isEmpty(addSelections) || !isEmpty(removeAccountIds);
	const columns = Columns(enableGroupActions ? (userId) => handleRemoveUser(userId) : undefined);
	const { tableColumns } = useTabletGridColumns<IProfileDataModel>({
		columns,
		excludedColumns: [UsersGridColumnsHeaders.Company, UsersGridColumnsHeaders.RegDate],
	});

	const resetPreviewCursor = useCallback((previewKey: string) => {
		previewKeyRef.current = previewKey;
		cursorRef.current = null;
		loadedCountRef.current = 0;
	}, []);

	const resetPreviewSnapshot = () => {
		previewKeyRef.current = "";
		snapshotTokenRef.current = null;
		cursorRef.current = null;
		loadedCountRef.current = 0;
	};

	const getSelectionRowId = ({ selectionType, value }: IAssignmentAccountSelectionModel) => {
		return selectionType === SelectionAccountsType.AccountId ? value : -selectionType * 100000000 - value;
	};

	const isAccountSelection = (selection: IAssignmentAccountSelectionModel, userId: number) => {
		return selection.selectionType === SelectionAccountsType.AccountId && selection.value === userId;
	};

	const handleRemoveUser = (userId: number) => {
		setMembersDelta((delta) => {
			if (delta.addSelections.some((selection) => isAccountSelection(selection, userId))) {
				return {
					...delta,
					addSelections: delta.addSelections.filter((selection) => !isAccountSelection(selection, userId)),
				};
			}

			if (delta.removeAccountIds.includes(userId)) return delta;

			return {
				...delta,
				removeAccountIds: [...delta.removeAccountIds, userId],
			};
		});
	};

	const handleAddUsers = async () => {
		const selectedUsers = await modalDialogService.show(AddUsersModal, {
			excludedUserIds: [
				...searchStore.items.map(({ id }) => id),
				...addSelections.map(getSelectionRowId),
			],
		});

		if (isEmpty(selectedUsers)) return;

		const selectedAccountIds = selectedUsers!
			.map(({ selection }) => selection)
			.filter(({ selectionType }) => selectionType === SelectionAccountsType.AccountId)
			.map(({ value }) => value);

		setMembersDelta((delta) => {
			const newSelections = selectedUsers!
				.map(({ selection }) => selection)
				.filter(
					(selection) =>
						!delta.addSelections.some(
							({ selectionType, value }) => selectionType === selection.selectionType && value === selection.value
						)
				);

			return {
				addSelections: [...delta.addSelections, ...newSelections],
				removeAccountIds: delta.removeAccountIds.filter((id) => !selectedAccountIds.includes(id)),
			};
		});
	};

	const handleSaveUsers = async () => {
		if (!isGridChanged) return;

		setIsSaving(true);
		try {
			await groupStore.commitGroupMembers(groupStore.groupData.id, {
				addSelections,
				removeAccountIds,
			});
			setMembersDelta(getEmptyMembersDelta());
			resetPreviewSnapshot();
			toasterStore.showSuccessMessage("Users saved");
		} catch (err) {
			toasterStore.showErrorMessage(err);
		} finally {
			setIsSaving(false);
		}
	};

	const getGroupUsers = useCallback(async (): Promise<IGetRecordsResponse<IProfileDataModel>> => {
		const previewKey = JSON.stringify({ addSelections, removeAccountIds });

		if (previewKeyRef.current !== previewKey) {
			resetPreviewCursor(previewKey);
		}

		const requestData = {
			addSelections,
			removeAccountIds,
			snapshotToken: snapshotTokenRef.current,
			cursor: cursorRef.current,
			take: previewMembersTake,
		} as IPreviewGroupMembersModel;
		const response = await groupStore.previewGroupMembers(groupStore.groupData.id, requestData);
		const { records, snapshotToken, nextCursor, hasMore } = response.data.data;
		const users = records.map(toProfileData);

		snapshotTokenRef.current = snapshotToken;
		cursorRef.current = nextCursor;
		loadedCountRef.current += users.length;

		return {
			records: users,
			totalFilteredRecords: loadedCountRef.current + (hasMore ? 1 : 0),
		};
	}, [addSelections, groupStore, removeAccountIds, resetPreviewCursor]);

	const fetchUsersData = useCallback(
		() => (enableGroupActions ? getGroupUsers() : accountStore.getAllUsers()),
		[accountStore, enableGroupActions, getGroupUsers]
	);

	const getColumns = () => {
		if (enableGroupActions) return columns;

		if (!bulkOperationsStore.isShowSelection) {
			const filteredColumns = tableColumns.slice(1);
			return filteredColumns;
		}
		return columns;
	};

	useBulkSelections([
		PermissionConstantsRead.OtherOwnCompanyUsersAdditionalInformation,
		PermissionConstantsRead.OtherAllUsersAdditionalInformation,
	]);

	return (
		<div className={styles.cms_users}>
			{enableGroupActions && <Prompt message={infoMessages.leaveWithoutSaving} when={isGridChanged} />}
			{!isHideFilter && <CmsUsersFilter />}
			{enableGroupActions && (
				<div className={styles.cms_users__group_actions}>
					<Button label="Add" onClick={handleAddUsers} className={styles.cms_users__group_action_btn} />
					<div className={styles.cms_users__save_action}>
						<Button
							label="Save"
							onClick={handleSaveUsers}
							disabled={!isGridChanged || isSaving}
							className={styles.cms_users__group_action_btn}
						/>
						<Tooltip
							label={"Adding a large number of users may take some time, and the changes may not appear immediately."}
							className={styles.cms_users__save_tooltip}
						>
							<InfoIcon />
						</Tooltip>
					</div>
				</div>
			)}
			<CmsSpreadsheet
				tableColumns={getColumns()}
				tileComponent={CmsTileUser}
				fetchData={fetchUsersData}
				profileRoute={Routes.CmsUserProfile}
				className={styles.cms_users__body}
			/>
		</div>
	);
};

export default observer(CmsUsers);
